import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Truck, MapPin, Calendar } from "lucide-react";

const emptyJob = {
  title: "", description: "", pickup_location: "", delivery_location: "",
  driver_id: "", driver_name: "", vehicle_id: "", status: "pending",
  priority: "medium", scheduled_date: "", estimated_delivery: "",
  cargo_type: "", weight: "", notes: ""
};

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyJob });
  const [filter, setFilter] = useState("all");

  const fetchData = () => {
    Promise.all([
      axios.get(`${API}/jobs`),
      axios.get(`${API}/drivers`)
    ]).then(([jobsRes, driversRes]) => {
      setJobs(jobsRes.data);
      setDrivers(driversRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    if (!form.title) { toast.error("Job title is required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/jobs/${editing.id}`, form);
        toast.success("Job updated");
      } else {
        await axios.post(`${API}/jobs`, form);
        toast.success("Job created");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm({ ...emptyJob });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    try { await axios.delete(`${API}/jobs/${id}`); toast.success("Deleted"); fetchData(); } catch { toast.error("Failed"); }
  };

  const openEdit = (job) => { setEditing(job); setForm({ ...job }); setDialogOpen(true); };
  const openNew = () => { setEditing(null); setForm({ ...emptyJob }); setDialogOpen(true); };

  const filtered = filter === "all" ? jobs : jobs.filter(j => j.status === filter);

  return (
    <div data-testid="jobs-page">
      <PageHeader title="Jobs & Tasks" subtitle={`${jobs.length} total jobs`}>
        <button data-testid="create-job-button" onClick={openNew}
          className="h-8 px-3 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] flex items-center gap-1.5 transition-colors active:scale-95">
          <Plus className="w-3.5 h-3.5" /> New Job
        </button>
      </PageHeader>

      <div className="p-6">
        {/* Status Filter */}
        <div className="flex gap-2 mb-4" data-testid="job-filters">
          {["all", "pending", "assigned", "in_transit", "delivered", "cancelled"].map(s => (
            <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
              className={`h-7 px-3 text-[10px] uppercase tracking-wider font-medium rounded-sm transition-colors
                ${filter === s ? "bg-orange-500/10 text-orange-500 border border-orange-500/20" : "text-zinc-500 hover:text-zinc-300 border border-zinc-800"}`}>
              {s.replace("_", " ")}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Truck className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm">No jobs found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {filtered.map((job) => (
              <div key={job.id} className="border border-zinc-800 bg-[#18181B] rounded-sm p-4 animate-fade-in hover:border-zinc-700 transition-colors" data-testid={`job-card-${job.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-heading text-sm font-bold text-white">{job.title}</h3>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{job.cargo_type && `${job.cargo_type} • `}{job.weight && `${job.weight} kg`}</p>
                  </div>
                  <StatusBadge status={job.status} />
                </div>

                <div className="space-y-2 mb-3">
                  {job.pickup_location && (
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <MapPin className="w-3 h-3 text-emerald-500" />
                      <span className="truncate">{job.pickup_location}</span>
                    </div>
                  )}
                  {job.delivery_location && (
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <MapPin className="w-3 h-3 text-red-500" />
                      <span className="truncate">{job.delivery_location}</span>
                    </div>
                  )}
                  {job.scheduled_date && (
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                      <Calendar className="w-3 h-3 text-blue-500" />
                      <span className="font-mono">{job.scheduled_date}</span>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-zinc-800/60">
                  <div className="flex items-center gap-2">
                    {job.driver_name && (
                      <span className="text-xs text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded-sm">{job.driver_name}</span>
                    )}
                    <StatusBadge status={job.priority} />
                  </div>
                  <div className="flex gap-1">
                    <button data-testid={`edit-job-${job.id}`} onClick={() => openEdit(job)} className="text-zinc-500 hover:text-zinc-200 p-1"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button data-testid={`delete-job-${job.id}`} onClick={() => handleDelete(job.id)} className="text-zinc-500 hover:text-red-400 p-1"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#18181B] border-zinc-800 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold tracking-tight">{editing ? "Edit Job" : "New Job"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Title *</Label>
              <Input data-testid="job-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Pickup</Label>
                <Input data-testid="job-pickup-input" value={form.pickup_location} onChange={e => setForm({ ...form, pickup_location: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Delivery</Label>
                <Input data-testid="job-delivery-input" value={form.delivery_location} onChange={e => setForm({ ...form, delivery_location: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Driver</Label>
                <select data-testid="job-driver-select" value={form.driver_id} onChange={e => {
                  const d = drivers.find(dr => dr.id === e.target.value);
                  setForm({ ...form, driver_id: e.target.value, driver_name: d?.name || "" });
                }} className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="">Unassigned</option>
                  {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Status</Label>
                <select data-testid="job-status-select" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="pending">Pending</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_transit">In Transit</option>
                  <option value="delivered">Delivered</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Priority</Label>
                <select data-testid="job-priority-select" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}
                  className="mt-1 w-full h-10 px-3 bg-zinc-900 border border-zinc-700 text-white rounded-[2px] text-sm">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Cargo Type</Label>
                <Input data-testid="job-cargo-input" value={form.cargo_type} onChange={e => setForm({ ...form, cargo_type: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Weight (kg)</Label>
                <Input data-testid="job-weight-input" value={form.weight} onChange={e => setForm({ ...form, weight: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Scheduled Date</Label>
                <Input data-testid="job-scheduled-date" type="date" value={form.scheduled_date} onChange={e => setForm({ ...form, scheduled_date: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Est. Delivery</Label>
                <Input data-testid="job-est-delivery" type="date" value={form.estimated_delivery} onChange={e => setForm({ ...form, estimated_delivery: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Notes</Label>
              <Textarea data-testid="job-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 text-white rounded-[2px]" rows={2} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button data-testid="cancel-job" onClick={() => setDialogOpen(false)} className="h-9 px-4 border border-zinc-700 text-zinc-300 text-xs font-medium uppercase tracking-wider rounded-[2px] hover:bg-zinc-800">Cancel</button>
              <button data-testid="save-job" onClick={handleSave} className="h-9 px-4 bg-orange-500 hover:bg-orange-600 text-white text-xs font-medium uppercase tracking-wider rounded-[2px] active:scale-95">
                {editing ? "Update" : "Create"} Job
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
